'use client'

import { Button } from '@qr-front/ui/components/Button'
import { Spinner } from '@qr-front/ui/components/Spinner'
import { useTranslations } from 'next-intl'

import { ROUTES } from '@/constants'
import { Link } from '@/i18n-routing'
import { THideSocials } from '@/utils'

import { InputFieldEmailForm } from './components/InputFieldEmailForm'
import { InputFieldPasswordForm } from './components/InputFieldPasswordForm'
import {
	APPLE_AUTH_TYPE,
	AppleAuthButton,
} from './components/social-auth/AppleAuthButton'
import { FacebookAuthButton } from './components/social-auth/FacebookAuthButton'
import { GoogleAuthButton } from './components/social-auth/GoogleAuthButton'
import { SignUpPageDisclaimer } from './SignUpPageDisclaimer'
import { DEFAULT_HIDE_SOCIALS, hasVisibleSocialAuth } from './utils/hideSocials'

type TSignUpPageDirectProps = {
	responseGoogle: (accessToken: string) => void
	responseFacebook: ({ accessToken }: { accessToken: string }) => void
	handleFacebookEmailDeclined?: () => void
	handleAppleSubmit: (token: string, signup?: boolean) => void
	handleAppleError?: (error: string) => void
	onSubmit: (
		event:
			| React.MouseEvent<HTMLButtonElement, MouseEvent>
			| React.KeyboardEvent<Element>,
	) => void
	isLoading: boolean
	hideSocials?: THideSocials
	hidePassword?: boolean
}

export function SignUpPageDirect({
	responseGoogle,
	responseFacebook,
	handleFacebookEmailDeclined,
	handleAppleSubmit,
	handleAppleError,
	onSubmit,
	isLoading,
	hideSocials = DEFAULT_HIDE_SOCIALS,
	hidePassword = false,
}: TSignUpPageDirectProps) {
	const t = useTranslations()

	const handleKeyDown = (e: React.KeyboardEvent) => {
		if (e.key === 'Enter') {
			e.preventDefault()
			onSubmit(e)
		}
	}

	return (
		<div className="my-[56px] flex w-full max-w-[350px] flex-row rounded-12 bg-white shadow-generator desktop:my-12 desktop:max-w-[488px]">
			<div className="flex w-full flex-col items-center gap-8 px-4 py-6 text-center desktop:items-start desktop:p-8 desktop:text-start">
				<form
					className="flex w-full flex-col gap-8"
					onKeyDown={handleKeyDown}
				>
					{/*Header*/}
					<div className="flex flex-col gap-2">
						<div className="type-heading-3 text-black">
							{t('auth__signup__title')}
						</div>
						<div className="type-body-medium text-dark-grey">
							{t('auth__signup__description')}
						</div>
					</div>
					<div className="flex flex-col gap-6">
						<div className="flex flex-col gap-4">
							{/* Input Login */}
							<InputFieldEmailForm />
							{/* Input Password */}
							{!hidePassword && <InputFieldPasswordForm />}
						</div>
						{/* Sign up button */}
						<Button
							visual="default"
							size="large"
							type="button"
							onClick={onSubmit}
							className="w-full"
							disabled={isLoading}
						>
							{t('auth__signup__submit')}
							{isLoading && <Spinner color="white" />}
						</Button>
					</div>
					{hasVisibleSocialAuth(hideSocials) && (
						<>
							{/* Divider */}
							<div className="relative w-full">
								<div className="absolute inset-0 flex items-center">
									<div className="w-full border-t border-border-grey-50"></div>
								</div>
								<div className="text-sm relative flex justify-center">
									<span className="bg-white px-4 text-grey">
										{t('auth__common__separator_title')}
									</span>
								</div>
							</div>
							{/* Social auth */}
							<div className="flex flex-col items-center gap-4">
								{!hideSocials.GOOGLE && (
									<GoogleAuthButton
										onSubmit={responseGoogle}
										variant="text"
									/>
								)}
								{!hideSocials.FACEBOOK && (
									<FacebookAuthButton
										onSubmit={responseFacebook}
										onEmailDeclined={
											handleFacebookEmailDeclined
										}
										variant="text"
									/>
								)}
								{!hideSocials.APPLE && (
									<AppleAuthButton
										onSubmit={handleAppleSubmit}
										onError={handleAppleError}
										authType={APPLE_AUTH_TYPE.Signup}
										variant="text"
									/>
								)}
							</div>
						</>
					)}
					{/* Forgot Password */}
					<div className="flex h-[22px] items-center justify-center gap-4 text-center desktop:justify-start">
						<span className="type-body-small text-dark-grey">
							{t('auth__signup__cta')}
						</span>
						<Link
							href={ROUTES.auth.login}
							className="type-button-small text-blue"
						>
							{t('auth__signup__cta_action')}
						</Link>
					</div>
					{/* Sign up disclaimer */}
					<SignUpPageDisclaimer />
				</form>
			</div>
		</div>
	)
}
